import React, { useState, useEffect } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { 
  Package, ShoppingBag, TrendingUp, MoreHorizontal, 
  Eye, ArrowUpRight, ArrowDownRight, DollarSign, Clock, Star,
  Truck, CheckCircle, Clock as ClockIcon, Calendar,
  Download, Search, Activity, Zap, Plus
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeller } from '../../context/SellerContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { products, orders, stats, loading } = useSeller();
  
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // Dummy data for charts
  const analysisData = [
    { name: 'Week 1', sales: 4000, orders: 24 },
    { name: 'Week 2', sales: 3000, orders: 13 },
    { name: 'Week 3', sales: 2000, orders: 98 },
    { name: 'Week 4', sales: 2780, orders: 39 },
    { name: 'Week 5', sales: 1890, orders: 48 },
    { name: 'Week 6', sales: 2390, orders: 38 },
    { name: 'Week 7', sales: 3490, orders: 43 },
  ];

  // Calculate dynamic category data
  const colors = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899'];
  const categoryDataRaw = products.reduce((acc, p) => {
    const cat = p.category || 'Other';
    const existing = acc.find(c => c.name === cat);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: cat, value: 1 });
    }
    return acc;
  }, []);
  const categoryData = categoryDataRaw.map((c, i) => ({ ...c, color: colors[i % colors.length] }));
  const recentOrders = orders.slice(0, 5).map(order => {
    let statusColor = '#f59e0b';
    let statusBg = '#fef3c7';
    let statusText = 'Processing';
    
    if (order.orderStatus === 'Processing') { statusColor = '#f59e0b'; statusBg = '#fef3c7'; statusText = 'Processing'; }
    if (order.orderStatus === 'Shipped') { statusColor = '#3b82f6'; statusBg = '#dbeafe'; statusText = 'Shipped'; }
    if (order.orderStatus === 'Delivered') { statusColor = '#10b981'; statusBg = '#d1fae5'; statusText = 'Delivered'; }
    if (order.orderStatus === 'Cancelled') { statusColor = '#ef4444'; statusBg = '#fee2e2'; statusText = 'Cancelled'; }

    return {
      id: order._id.slice(-8).toUpperCase(),
      product: order.orderItems[0]?.name || 'Multiple Items',
      qty: order.orderItems[0]?.quantity || 1,
      status: statusText,
      statusColor,
      statusBg,
      amount: order.totalAmount,
      date: new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  });

  const topProducts = [...products].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 4).map(p => ({
    id: p._id,
    name: p.name,
    price: `₹${p.price}`,
    sales: p.numReviews || Math.floor(Math.random() * 50) + 10,
    image: p.images?.[0]?.url || 'https://images.unsplash.com/photo-1614594975525-e45190c55d40?w=150&h=150&fit=crop',
    stock: p.countInStock,
    rating: p.rating || 0
  }));

  const StatCard = ({ icon: Icon, title, value, trend, trendUp, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-card-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div className="stat-card-info">
        <p className="stat-card-title">{title}</p>
        <h3 className="stat-card-value">{value}</h3>
        {trend && (
          <div className="stat-card-trend">
            {trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            <span style={{ color: trendUp ? '#10b981' : '#ef4444' }}>{trend}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner-border text-success" role="status" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="seller-dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">Welcome back! Here's what's happening with your store today.</p>
        </div>
        <div className="dashboard-actions">
          <button className="btn-outline-custom">
            <Download size={16} />
            Export Report
          </button>
          <button className="btn-primary-custom">
            <Plus size={16} />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats Grid - Only 3 Cards */}
      <div className="stats-grid-3">
        <StatCard 
          icon={Package} title="Total Products" value={stats.totalProducts}
          trend="+12%" trendUp={true} color="#10b981" bgColor="#d1fae5"
        />
        <StatCard 
          icon={ShoppingBag} title="Total Orders" value={stats.totalOrders}
          trend="+8%" trendUp={true} color="#3b82f6" bgColor="#dbeafe"
        />
        <StatCard 
          icon={DollarSign} title="Total Earnings" value={`₹${stats.totalEarnings.toLocaleString()}`}
          trend="+23%" trendUp={true} color="#f59e0b" bgColor="#fed7aa"
        />
      </div>

      {/* Charts Section */}
      <div className="charts-row">
        <div className="chart-large">
          <div className="chart-header">
            <div>
              <h5 className="chart-title">Sales Overview</h5>
              <p className="chart-subtitle">Monthly earnings & order performance</p>
            </div>
            <select className="period-select" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analysisData}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                />
                <Area type="monotone" dataKey="sales" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-small">
          <div className="chart-header">
            <h5 className="chart-title">Category Distribution</h5>
            <p className="chart-subtitle">Products by category</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend 
                  iconType="circle"
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value) => <span style={{ fontSize: '12px' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="bottom-section">
        {/* Top Products */}
        <div className="top-products">
          <div className="section-header">
            <div>
              <h5 className="section-title">Top Selling Products</h5>
              <p className="section-subtitle">Best performing items this month</p>
            </div>
            <Link to="/seller/products" className="view-all-link">View All →</Link>
          </div>
          <div className="products-grid">
            {topProducts.map((prod, idx) => (
              <div key={prod.id} className="product-card">
                <div className="product-image">
                  <img src={prod.image} alt={prod.name} />
                </div>
                <div className="product-details">
                  <h6 className="product-name">{prod.name}</h6>
                  <div className="product-rating">
                    <Star size={12} fill="#f59e0b" color="#f59e0b" />
                    <span>{prod.rating}</span>
                  </div>
                  <div className="product-price">{prod.price}</div>
                  <div className="product-stats">
                    <span>📦 {prod.sales} sold</span>
                    <span>📊 Stock: {prod.stock}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="recent-orders">
          <div className="section-header">
            <div>
              <h5 className="section-title">Recent Orders</h5>
              <p className="section-subtitle">Latest transactions from customers</p>
            </div>
            <Link to="/seller/orders" className="view-all-link">View All →</Link>
          </div>
          <div className="orders-list">
            {recentOrders.map((order, idx) => (
              <div key={idx} className="order-item">
                <div className="order-info">
                  <div className="order-id">
                    <span className="order-hash">#</span>{order.id}
                  </div>
                  <div className="order-product">{order.product}</div>
                  <div className="order-meta">
                    <span className="order-qty">Qty: {order.qty}</span>
                    <span className="order-amount">₹{order.amount}</span>
                  </div>
                </div>
                <div className="order-status">
                  <span className="status-badge" style={{ backgroundColor: order.statusBg, color: order.statusColor }}>
                    {order.status === 'Processing' && <ClockIcon size={12} />}
                    {order.status === 'Shipped' && <Truck size={12} />}
                    {order.status === 'Delivered' && <CheckCircle size={12} />}
                    {order.status}
                  </span>
                  <div className="order-date">
                    <Calendar size={12} />
                    {order.date}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`.seller-dashboard {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}

.dashboard-title {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.dashboard-actions {
  display: flex;
  gap: 12px;
}

.btn-outline-custom,
.btn-primary-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-outline-custom {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.btn-outline-custom:hover {
  border-color: #10b981;
  color: #10b981;
  transform: translateY(-2px);
}

.btn-primary-custom {
  background: linear-gradient(135deg, #2d6a4f 0%, #1a472a 100%);
  border: none;
  color: white;
}

.btn-primary-custom:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(45, 106, 79, 0.3);
}

.stats-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  border: 1px solid #e8ecef;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.stat-card-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
}

.stat-card-info {
  flex: 1;
}

.stat-card-title {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 4px 0;
  font-weight: 500;
}

.stat-card-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.stat-card-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  margin-top: 6px;
}

.charts-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.chart-large,
.chart-small {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.period-select {
  padding: 6px 12px;
  border-radius: 40px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 13px;
  color: #475569;
}

.chart-container {
  height: 280px;
}

.bottom-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.top-products,
.recent-orders {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.view-all-link {
  color: #10b981;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.view-all-link:hover {
  color: #059669;
  transform: translateX(4px);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.product-card {
  background: #f8fafc;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
}

.product-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  z-index: 1;
}

.product-image {
  position: relative;
  height: 140px;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.stock-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  background: #ef4444;
  color: white;
}

.product-details {
  padding: 12px;
}

.product-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-rating {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.product-rating span {
  font-size: 11px;
  color: #f59e0b;
  font-weight: 600;
}

.product-price {
  font-size: 14px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 6px;
}

.product-stats {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #94a3b8;
  padding-top: 6px;
  border-top: 1px solid #e2e8f0;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.order-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.order-info {
  flex: 1;
}

.order-id {
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.order-hash {
  color: #10b981;
}

.order-product {
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;
}

.order-meta {
  display: flex;
  gap: 16px;
  font-size: 11px;
  color: #94a3b8;
}

.order-status {
  text-align: right;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 6px;
}

.order-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.dashboard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
}

@media (max-width: 1200px) {
  .stats-grid-3 {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }

  .bottom-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .charts-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .seller-dashboard {
    padding: 16px;
  }

  .stats-grid-3 {
    grid-template-columns: 1fr;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }
}`}</style>
    </div>
  );
};

export default Dashboard;